import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

/**
 * Compresses an image client-side to WebP (max 1200px dimension, 85% quality).
 * Keeps file size well below 200KB to prevent Nginx 413 "Request Entity Too Large".
 */
export const compressImageToWebP = async (
  file: File,
  maxWidth = 1200,
  maxHeight = 1200,
  quality = 0.85
): Promise<File> => {
  // If not an image or SVG/GIF, return original file
  if (!file.type.startsWith("image/") || file.type === "image/gif" || file.type === "image/svg+xml") {
    return file;
  }

  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      let { width, height } = img;
      if (width > maxWidth || height > maxHeight) {
        if (width / height > maxWidth / maxHeight) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        } else {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return resolve(file);
      }

      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            return resolve(file);
          }
          const baseName = file.name.substring(0, file.name.lastIndexOf(".")) || file.name;
          const compressedFile = new File([blob], `${baseName}.webp`, {
            type: "image/webp",
            lastModified: Date.now(),
          });
          resolve(compressedFile);
        },
        "image/webp",
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };

    img.src = objectUrl;
  });
};

export const uploadApi = {
  uploadRecipeImage: async (file: File): Promise<{ imageUrl: string }> => {
    const formData = new FormData();
    formData.append("image", file);
    const response = await axios.post(`${API_URL}/api/admin/upload/recipe-image`, formData, {
      headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` },
    });
    return response.data;
  },
  uploadImage: async (file: File, prefix?: string): Promise<{ imageUrl: string; message: string }> => {
    // Automatically compress to WebP before sending to backend to bypass Nginx 1MB client_max_body_size
    const fileToUpload = await compressImageToWebP(file);

    const formData = new FormData();
    formData.append("image", fileToUpload);
    if (prefix) {
      formData.append("prefix", prefix);
    }

    const response = await axios.post(`${API_URL}/api/admin/upload`, formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  },
};
